<template>
  <RevLoadingScreen
    v-if="!offer"
    class="my-auto"
    text="Your offer is loading"
  />
  <div v-else class="flex flex-col justify-center gap-24 md:gap-32">
    <div
      class="flex max-w-full grow flex-col gap-24 p-24 pb-0 md:mx-auto md:w-[800px] md:gap-32 lg:basis-full"
    >
      <div
        class="border-static-default-low relative flex items-center justify-center border-b px-40 pb-20 pt-8"
      >
        <h1 class="body-1">{{ i18n(translations.title) }}</h1>
      </div>

      <div class="flex flex-col items-center">
        <span v-if="offer.tier === 'low'" class="heading-2 mb-8">
          {{ i18n(translations.priceOffer) }}
        </span>
        <span v-else class="heading-2 mb-8">
          {{ i18n(translations.congratsOffer) }}
        </span>

        <AnimatedPrice
          :animation-delay="0"
          class="mb-32"
          :display-asterix="false"
          :end-price="offer.listing.price"
          :start-price="{ amount: '0', currency: offer.listing.price.currency }"
        />

        <span class="text-static-default-hi body-1">
          {{ i18n(translations.reassuranceOffer) }}
        </span>
      </div>

      <div class="flex flex-col gap-12">
        <h2 class="body-1-bold">{{ i18n(translations.summary) }}</h2>

        <OfferSummary :summary="offer.funnelSummary" />
      </div>

      <ReassuranceBanner class="order-4" />

      <OfferGraph
        v-if="experiments['experiment.newOfferPageV3'] !== 'withoutGraph'"
        data-test="offer-graph"
        :device-name="offer.product.shortTitle"
        :price="offer.listing.price"
        :tiering="offer.tier"
      />
    </div>

    <section
      class="bg-surface-default-low mb-[105px] w-full py-40 md:mb-[70px]"
    >
      <div
        class="mx-auto flex flex-col md:w-[800px] md:px-24 lg:px-40 lg:py-56"
      >
        <FaqBlock />
      </div>
    </section>

    <RevStickyBar position="bottom">
      <div class="md:mx-auto md:w-[800px] md:px-24">
        <div class="flex w-full justify-end">
          <div
            class="flex w-full items-center justify-end gap-12 md:w-1/2 md:gap-24"
          >
            <RevButton
              data-test="decline"
              full-width="always"
              variant="secondary"
              @click="declineOffer"
            >
              {{ i18n(translations.declineOffer) }}
            </RevButton>
            <RevButton
              data-test="accept"
              full-width="always"
              variant="primary"
              @click="acceptOffer"
            >
              {{ i18n(translations.acceptOffer) }}
            </RevButton>
          </div>
        </div>
      </div>
    </RevStickyBar>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, inject, onMounted, ref } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevStickyBar } from '@ds/components/StickyBar'

import { CMS } from '~/scopes/cms/routes-names'

import FaqBlock from '../components/FaqBlock/FaqBlock.vue'
import OfferGraph from '../components/OfferGraph/OfferGraph.vue'
import OfferSummary from '../components/OfferSummary/OfferSummary.vue'
import ReassuranceBanner from '../components/ReassuranceBanner/ReassuranceBanner.vue'
import type { ErrorData } from '../components/TheCatcher/useCatcher'
import { useBuybackFunnelOffer } from '../composables/useBuybackFunnelOffer'
import { ROUTE_NAMES } from '../constants'
import AnimatedPrice from '../swap/components/Intro/AnimatedPrice.vue'

import translations from './Offer.translations'
import { FUNNEL_ERROR_MESSAGE } from './constants'

const cmsHomePage = {
  name: CMS.BUYBACK,
  params: {
    pageName: 'home',
  },
}

const { query, params } = useRoute()
const { offer, offerPayload } = useBuybackFunnelOffer()
const { trackClick } = useTracking()
const i18n = useI18n()
const experiments = useExperiments()

const errorData = inject<ErrorData>('errorData', ref({ message: '', step: '' }))
errorData.value = {
  message: FUNNEL_ERROR_MESSAGE.BUYBACK_FUNNEL,
  step: 'Offer',
}
const nextStep = computed(() => {
  return offer.value?.bankDetailsRequired
    ? {
        name: ROUTE_NAMES.BANK,
        params,
        query,
      }
    : {
        name: ROUTE_NAMES.ADDRESS,
        params,
        query,
      }
})

function trackAction(name: string) {
  trackClick({
    zone: 'buyback',
    name,
    value: {
      listing_public_id: offer.value?.listing.listingPublicId,
      estimation: offer.value?.listing.price.amount.toString(),
      ...offerPayload.value,
    },
  })
}

async function acceptOffer() {
  trackAction('accept_offer')
  await navigateTo(nextStep.value)
}

async function declineOffer() {
  trackAction('decline_offer')
  await navigateTo(cmsHomePage)
}

onMounted(async () => {
  if (!offer.value) {
    await navigateTo(cmsHomePage)
  }
})
</script>
